import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { isRunningStandalone } from '@/lib/pwa';

const UPDATE_TOAST_ID = 'pwa-update-available';
const OFFLINE_TOAST_ID = 'pwa-offline-ready';

// Check for SW updates every minute so a fresh deploy is picked up quickly.
const SW_CHECK_INTERVAL = 60 * 1000;

/**
 * On n'enregistre le Service Worker QUE pour l'app installée (PWA standalone).
 * En navigateur classique, aucun SW n'est enregistré → une actualisation charge
 * toujours la dernière version servie par nginx (pas d'interception du shell).
 * Le hook `useRegisterSW` vit donc dans le composant interne, monté seulement
 * en standalone.
 */
export function PwaUpdatePrompt() {
  // display-mode est stable pour la session → on l'évalue une fois au montage.
  const [standalone] = useState(() => isRunningStandalone());
  if (!standalone) return null;
  return <PwaUpdatePromptStandalone />;
}

function PwaUpdatePromptStandalone() {
  const shownRefreshToastRef = useRef(false);
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);

  const {
    needRefresh: [needRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker,
  } = useRegisterSW({
    immediate: true,
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      registrationRef.current = registration;
      // Check immediately on load, then poll periodically.
      void registration.update();
      setInterval(() => {
        void registration.update();
      }, SW_CHECK_INTERVAL);
    },
  });

  // Re-check for an update whenever the tab comes back to the foreground.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void registrationRef.current?.update();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Activate the waiting worker before React Router starts the next link
  // navigation. We move the history URL first, then let the SW-controlled
  // reload boot the new bundle directly on the requested destination.
  useEffect(() => {
    if (!needRefresh) return;

    const handleNextNavigation = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const anchor = (event.target as Element | null)?.closest<HTMLAnchorElement>('a[href]');
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;

      const destination = new URL(anchor.href, window.location.href);
      if (destination.origin !== window.location.origin) return;

      event.preventDefault();
      window.history.replaceState(window.history.state, '', destination.href);
      void updateServiceWorker(true);
    };

    document.addEventListener('click', handleNextNavigation, true);
    return () => document.removeEventListener('click', handleNextNavigation, true);
  }, [needRefresh, updateServiceWorker]);

  useEffect(() => {
    if (!offlineReady) {
      return;
    }

    toast.success('Mode hors ligne active', {
      id: OFFLINE_TOAST_ID,
      description: "L'application peut maintenant fonctionner sans reseau.",
    });

    setOfflineReady(false);
  }, [offlineReady, setOfflineReady]);

  // Safety net: if the user stays on the same page (never navigates), surface a
  // non-blocking toast so they can apply the update on demand.
  useEffect(() => {
    if (!needRefresh || shownRefreshToastRef.current) {
      return;
    }

    shownRefreshToastRef.current = true;

    toast('Mise a jour disponible', {
      id: UPDATE_TOAST_ID,
      duration: Infinity,
      description: "Une nouvelle version est prete. Elle s'appliquera automatiquement.",
      action: {
        label: 'Recharger maintenant',
        onClick: () => {
          void updateServiceWorker(true);
          toast.dismiss(UPDATE_TOAST_ID);
        },
      },
    });
  }, [needRefresh, updateServiceWorker]);

  useEffect(() => {
    if (!needRefresh) {
      shownRefreshToastRef.current = false;
      toast.dismiss(UPDATE_TOAST_ID);
    }
  }, [needRefresh]);

  return null;
}
